import type { Timeline } from '@val/shared'
import { getParty } from '@val/shared'
import { useMemo } from 'react'
import { pct } from '../lib/format.ts'
import './counttimeline.css'

interface Props {
  timeline: Timeline
  highlightParty?: string
}

const W = 760
const H = 220
const PAD = { top: 16, right: 16, bottom: 28, left: 30 }

export function CountTimeline({ timeline, highlightParty }: Props) {
  const { points } = timeline

  const { lines, maxSeats } = useMemo(() => {
    const codes = new Set<string>()
    for (const pt of points) for (const c of Object.keys(pt.seats)) codes.add(c)
    let max = 1
    for (const pt of points) for (const v of Object.values(pt.seats)) max = Math.max(max, v)
    const series = [...codes].map((code) => ({
      code,
      values: points.map((pt) => pt.seats[code] ?? 0),
      last: points.at(-1)?.seats[code] ?? 0,
    }))
    series.sort((a, b) => b.last - a.last)
    return { lines: series, maxSeats: max }
  }, [points])

  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom
  const x = (i: number) => PAD.left + (points.length === 1 ? 0 : (i / (points.length - 1)) * innerW)
  const y = (v: number) => PAD.top + innerH - (v / maxSeats) * innerH

  return (
    <div className="counttimeline">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="counttimeline__svg"
        role="img"
        aria-label="Mandat över tid"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((g) => (
          <line
            key={g}
            x1={PAD.left}
            x2={W - PAD.right}
            y1={PAD.top + innerH - g * innerH}
            y2={PAD.top + innerH - g * innerH}
            className="counttimeline__grid"
          />
        ))}
        <text x={PAD.left} y={H - 8} className="counttimeline__axis">
          0 %
        </text>
        <text x={W - PAD.right} y={H - 8} className="counttimeline__axis" textAnchor="end">
          {pct((points.at(-1)?.countedRatio ?? 0) * 100)} räknat
        </text>

        {lines.map((s) => {
          const meta = getParty(s.code)
          const isHi = s.code === highlightParty
          const d = s.values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ')
          return (
            <path
              key={s.code}
              d={d}
              fill="none"
              stroke={meta.color}
              strokeWidth={isHi ? 3.5 : 1.8}
              strokeOpacity={isHi ? 1 : 0.55}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )
        })}
      </svg>

      <div className="counttimeline__legend">
        {lines.slice(0, 8).map((s) => {
          const meta = getParty(s.code)
          return (
            <span
              key={s.code}
              className={`counttimeline__key${s.code === highlightParty ? ' is-hi' : ''}`}
            >
              <span className="counttimeline__swatch" style={{ background: meta.color }} />
              {meta.shortName} {s.last}
            </span>
          )
        })}
      </div>
    </div>
  )
}
